import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  BookmarkIcon, 
  PlusIcon,
  CheckIcon 
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { savedAPI, createQueryKey } from '../services/api';
import { useMobile } from '../hooks/useMobile';

const BookmarkModal = ({ isOpen, onClose, projectId, projectName }) => {
  const { isMobile } = useMobile();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const queryClient = useQueryClient();

  // Get user's saved lists
  const { data: listsResponse, isLoading: listsLoading } = useQuery({
    queryKey: createQueryKey.savedLists(),
    queryFn: () => savedAPI.getLists(),
    enabled: isOpen
  });

  // Get project's saved status
  const { data: statusResponse } = useQuery({
    queryKey: createQueryKey.projectSavedStatus(projectId),
    queryFn: () => savedAPI.getProjectStatus(projectId),
    enabled: isOpen && !!projectId
  });

  const savedLists = listsResponse?.data?.data?.lists || [];
  const savedInLists = statusResponse?.data?.data?.savedInLists || [];

  // Create new list mutation
  const createListMutation = useMutation({
    mutationFn: (data) => savedAPI.createList(data),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.savedLists());
      setShowCreateForm(false);
      setNewListName('');
      setNewListDescription('');
      setIsPublic(true);
      toast.success('List created successfully!');
    },
    onError: (error) => {
      toast.error('Failed to create list: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Add/remove item mutations
  const addItemMutation = useMutation({
    mutationFn: (listId) => savedAPI.addItem(listId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.savedLists());
      queryClient.invalidateQueries(createQueryKey.projectSavedStatus(projectId));
      // Ensure ExplorePage bulk saved-status cache updates immediately
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey && query.queryKey[0] === 'project-saved-status-bulk'
      });
      // Also invalidate the saved page queries to update trail counts
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'saved-lists-page'
      });
    },
    onError: (error) => {
      toast.error('Failed to add to list: ' + (error.response?.data?.detail || error.message));
    }
  });

  const removeItemMutation = useMutation({
    mutationFn: (listId) => savedAPI.removeItem(listId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.savedLists());
      queryClient.invalidateQueries(createQueryKey.projectSavedStatus(projectId));
      // Ensure ExplorePage bulk saved-status cache updates immediately
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey && query.queryKey[0] === 'project-saved-status-bulk'
      });
      // Also invalidate the saved page queries to update trail counts
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'saved-lists-page'
      });
    },
    onError: (error) => {
      toast.error('Failed to remove from list: ' + (error.response?.data?.detail || error.message));
    }
  });

  const handleCreateList = (e) => {
    // Handle both form submit and direct click
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!newListName.trim()) {
      toast.error('List name is required');
      return;
    }

    createListMutation.mutate({
      name: newListName.trim(),
      description: newListDescription.trim() || null,
      isPublic
    });
  };

  const handleToggleList = (listId, isCurrentlySaved) => {
    if (isCurrentlySaved) {
      removeItemMutation.mutate(listId);
    } else {
      addItemMutation.mutate(listId);
    }
  };

  // Animation variants for bottom slide (mobile) and fade/scale (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Animation transition settings  
  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1], // Material Design easing
    duration: isMobile ? 0.35 : 0.25, // Slightly longer for mobile slide, shorter for desktop fade
  };

  // Background transition (faster fade)
  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.2,
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className={`fixed inset-0 bg-black bg-opacity-50 z-[50001] ${
            isMobile 
              ? 'flex items-end justify-center' 
              : 'flex items-center justify-center p-4'
          }`}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={backgroundTransition}
          onClick={onClose}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-800 w-full flex flex-col ${
              isMobile 
                ? 'rounded-t-xl' 
                : 'rounded-lg max-w-md max-h-[80vh]'
            }`}
            style={isMobile ? {
              height: 'auto',
              maxHeight: '75dvh',
              paddingBottom: 'env(safe-area-inset-bottom)'
            } : {}}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 flex-1 overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {showCreateForm ? 'Create New List' : 'Save to a list'}
                </h2>
                {showCreateForm && (
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewListName('');
                      setNewListDescription('');
                      setIsPublic(true);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>

              {/* Show either the list selection or create form */}
              {showCreateForm ? (
                /* Create new list form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      List name *
                    </label>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="e.g., Weekend Hikes"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      maxLength={100}
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      placeholder="Optional description for your list..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      maxLength={500}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Privacy
                    </label>
                    <select
                      value={isPublic ? 'public' : 'private'}
                      onChange={(e) => setIsPublic(e.target.value === 'public')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="public">Public - Anyone can discover and view this list</option>
                      <option value="private">Private - Only you can view this list</option>
                    </select>
                  </div>
                </div>
              ) : (
                /* List selection view */
                <div>
                  {/* Lists */}
                  {listsLoading ? (
                    <div className="space-y-3 mb-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-12 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4">
                      {savedLists.map((list) => {
                        const isInList = savedInLists.includes(list.id);
                        const isLoading = addItemMutation.isLoading || removeItemMutation.isLoading;
                        
                        return (
                          <button
                            key={list.id}
                            onClick={() => handleToggleList(list.id, isInList)}
                            disabled={isLoading}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left disabled:opacity-50 ${
                              isInList
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex-shrink-0">
                              {isInList ? (
                                <BookmarkSolidIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <BookmarkIcon className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                  {list.name}
                                </p>
                                {list.isDefault && (
                                  <span className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                              {list.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {list.description}
                                </p>
                              )}
                            </div>

                            {isInList && (
                              <CheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Create new list button */}
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full flex items-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Create new list</span>
                  </button>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              {showCreateForm ? (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewListName('');
                      setNewListDescription('');
                      setIsPublic(true);
                    }}
                    className="flex-1 px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateList}
                    disabled={createListMutation.isLoading || !newListName.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-3 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createListMutation.isLoading ? 'Creating...' : 'Create List'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={onClose}
                  className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-3 rounded-md transition-colors font-medium"
                >
                  Done
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BookmarkModal;
